import { useEffect, useRef, useState } from "react";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { Crop as CropState, PixelCrop, centerCrop, convertToPixelCrop, makeAspectCrop } from "react-image-crop";

import { LIMIT_10_MB } from "../../../common/constants/image";

import { Crop, CroppedFile } from "./Crop";
import { canvasPreview } from "./cropPreview";

// eslint-disable-next-line import/no-cycle

const CROP_RATIO_VERTICAL = 3 / 4;
const CROP_RATIO_HORIZONTAL = 4 / 3;
const CROP_RATIO_SQUARE = 1;

const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
  return centerCrop(makeAspectCrop({ unit: "%", width: 99 }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
};

const centerFreeCrop = (mediaWidth: number, mediaHeight: number) => {
  return centerCrop({ unit: "%", width: 100, height: 100 }, mediaWidth, mediaHeight);
};

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ImageDimensions } from "@/common/types/image";
import { getSizeInBite } from "@/components/ImageUploader/utils";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { ArrowForward } from "@/icons/outlined";

const CropVerticalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="8" y="3" width="8" height="18" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const CropHorizontalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="8" width="18" height="8" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const CropSquareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export interface CropDialogProps {
  onCrop: (image: string) => void;
  onClose: () => void;
  croppedFile?: CroppedFile;
  isOpen: boolean;
  ratio: number | undefined;
  maxDimensions: ImageDimensions | undefined;
  actualImageMetaData:
    | {
        width: number;
        height: number;
        size: number;
        extension: string;
      }
    | undefined;
  fillImageMetadata: (url: string) => void;
  handlePickImage: () => void;
  amplitudePayload?: Record<string, unknown>;
  selectImageModalOpen?: boolean;
  showRatioPresets?: boolean;
}

enum UploadInfo {
  ImageSizeLimitOverflow = "ImageSizeLimitOverflow",
  ImageNotFount = "ImageNotFount",
}

export const CropDialog = ({
  onCrop,
  onClose,
  croppedFile,
  isOpen,
  ratio,
  maxDimensions,
  actualImageMetaData,
  fillImageMetadata,
  handlePickImage,
  amplitudePayload,
  selectImageModalOpen,
  showRatioPresets = false,
}: CropDialogProps) => {
  const { t } = useTranslate();

  const { logAmplitudeEvent } = useAmplitude();

  const [info, setInfo] = useState<
    { value: UploadInfo; severity: "warning" | "brand" | "danger" | "positive" } | undefined
  >(undefined);
  const [image, setImage] = useState<string | undefined>(undefined);
  const [rotate, setRotate] = useState(0);
  const [selectedRatio, setSelectedRatio] = useState<number | undefined>(undefined);
  const [crop, setCrop] = useState<CropState | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>(); // control the canvas
  const [imageCroppedByUser, setImageCroppedByUser] = useState(false); //follow if image was cropped by user
  const imageDownscaled = useRef(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleAmplitudeEvent = async (event: AmplitudeEvents) => {
    logAmplitudeEvent(event, {
      ...(event === AmplitudeEvents.ImageUploaderCropConfirmed && {
        new_image_cropped: imageCroppedByUser,
        new_image_size_modified: imageDownscaled.current ? "Downscaled" : "No modification",
      }),
      original_image_size: actualImageMetaData?.size,
      original_image_width: actualImageMetaData?.width,
      original_image_height: actualImageMetaData?.height,
      original_image_extension: actualImageMetaData?.extension,
      ...amplitudePayload,
    });
  };

  const handleChange = (src: string) => {
    if (!src) {
      setInfo({ value: UploadInfo.ImageNotFount, severity: "danger" });
    }
    if (getSizeInBite(src) > LIMIT_10_MB) {
      setInfo({ value: UploadInfo.ImageSizeLimitOverflow, severity: "danger" });
    } else {
      setImage(src);
    }
    fillImageMetadata(src);
  };

  const validateCrop = async () => {
    if (!completedCrop) {
      return;
    }
    // when width and height are too small, the data url is empty: "data:,"
    if (completedCrop.width > 1 && completedCrop.height > 1 && imgRef?.current && previewCanvasRef?.current) {
      const { image, isCompressed } = await canvasPreview(
        croppedFile?.type as string,
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
        maxDimensions,
        rotate
      );
      imageDownscaled.current = isCompressed;
      handleChange(image);
      return image;
    }
  };

  useEffect(() => {
    validateCrop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCrop]);

  const applyCrop = (activeRatio: number | undefined, width: number, height: number) => {
    const percentCrop = activeRatio ? centerAspectCrop(width, height, activeRatio) : centerFreeCrop(width, height);
    setCrop(percentCrop);
    setCompletedCrop(convertToPixelCrop(percentCrop, width, height));
  };

  const applyRatioPreset = (newRatio: number) => {
    setSelectedRatio(newRatio);
    if (!imgRef.current) {
      return;
    }
    const { width, height } = imgRef.current;
    applyCrop(newRatio, width, height);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    applyCrop(selectedRatio ?? ratio, width, height);
  };

  const handleRotate = () => {
    handleAmplitudeEvent(AmplitudeEvents.ImageUploaderImageRotated);
    setRotate((rotate) => rotate + 90);
  };

  const handleClose = () => {
    handleAmplitudeEvent(AmplitudeEvents.ImageUploaderFlowCancelled);
    onClose();
  };

  const handleCrop = async () => {
    handleAmplitudeEvent(AmplitudeEvents.ImageUploaderCropConfirmed);
    //We revalidate to be sure to never overcome the size limit
    const image = await validateCrop();
    if (image) {
      onCrop(image);
    }
  };

  const handleReplace = () => {
    handlePickImage();
    setInfo(undefined);
  };

  const isSaveDisabled = info?.severity === "danger" || !image || !completedCrop;

  return (
    <DashboardDialog open={isOpen} onClose={handleClose} maxWidth="md">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader title={t("dashboard", "cropDialog.title")} />,
          content: (
            <DashboardDialogContent sx={{ padding: 0 }}>
              <Grid container>
                {info && (
                  <Grid item xs={12} sx={{ padding: 1 }}>
                    <DashboardInfobox vibe={info.severity}>
                      {info.value === UploadInfo.ImageNotFount
                        ? t("dashboard", "cropDialog.imageNotFound")
                        : info.value === UploadInfo.ImageSizeLimitOverflow
                          ? t("dashboard", "cropDialog.imageSizeLimitOverflow")
                          : t("dashboard", "cropDialog.unknownError")}
                    </DashboardInfobox>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sx={{
                    overflowY: "auto",
                    maxHeight: "60vh",
                    ["@supports (max-height: 60dvh)"]: {
                      maxHeight: "60dvh",
                    },
                    display: "flex",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <div>
                    <Crop
                      key={croppedFile?.url ? croppedFile?.url : "empty"}
                      setImageCroppedByUser={setImageCroppedByUser}
                      croppedFile={croppedFile}
                      ratio={selectedRatio ?? ratio}
                      rotate={rotate}
                      handleAmplitudeEvent={handleAmplitudeEvent}
                      setCompletedCrop={setCompletedCrop}
                      completedCrop={completedCrop}
                      imgRef={imgRef}
                      previewCanvasRef={previewCanvasRef}
                      onImageLoad={handleImageLoad}
                      crop={crop}
                      setCrop={setCrop}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Grid container justifyContent="center" alignItems="center" spacing={2}>
                    {showRatioPresets ? (
                      <>
                        <Grid item>
                          <DashboardButton
                            variant={selectedRatio === CROP_RATIO_VERTICAL ? "tonal" : "text"}
                            size="small"
                            vibe="brand"
                            onClick={() => applyRatioPreset(CROP_RATIO_VERTICAL)}
                            startIcon={<CropVerticalIcon />}
                          >
                            {t("dashboard", "cropDialog.ratioVertical")}
                          </DashboardButton>
                        </Grid>
                        <Grid item>
                          <DashboardButton
                            variant={selectedRatio === CROP_RATIO_HORIZONTAL ? "tonal" : "text"}
                            size="small"
                            vibe="brand"
                            onClick={() => applyRatioPreset(CROP_RATIO_HORIZONTAL)}
                            startIcon={<CropHorizontalIcon />}
                          >
                            {t("dashboard", "cropDialog.ratioHorizontal")}
                          </DashboardButton>
                        </Grid>
                        <Grid item>
                          <DashboardButton
                            variant={selectedRatio === CROP_RATIO_SQUARE ? "tonal" : "text"}
                            size="small"
                            vibe="brand"
                            onClick={() => applyRatioPreset(CROP_RATIO_SQUARE)}
                            startIcon={<CropSquareIcon />}
                          >
                            {t("dashboard", "cropDialog.ratioSquare")}
                          </DashboardButton>
                        </Grid>
                      </>
                    ) : (
                      <Grid item>
                        <DashboardButton
                          variant="text"
                          size="medium"
                          vibe="brand"
                          onClick={handleRotate}
                          startIcon={<ArrowForward />}
                        >
                          {t("common", "rotate")}
                        </DashboardButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={handleCrop} disabled={isSaveDisabled} data-test="crop-validate">
                {t("dashboard", "common.save")}
              </DashboardButton>
              <Stack direction="row" gap={1}>
                {!selectImageModalOpen && (
                  <DashboardButton onClick={handleReplace} vibe="neutral" variant="outlined">
                    {t("dashboard", "common.replace")}
                  </DashboardButton>
                )}
              </Stack>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
